import {
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { PerformanceLog, TrainingLog } from "../types/post";
import {
  AddPerformanceResult,
  PerformanceDraft,
  PerformanceResultDraft,
} from "../types/performance";
import { eventDayIndex } from "../types/event";
import {
  addLogIDToDayPost,
  addNiggleLog,
  addPerformanceLog,
  addTrainingLog,
  deletePerformanceLog,
  getPerformanceLog,
  getTrainingLog,
  removeLogIDFromDayPost,
} from "./dayPostService";
import { sessionService } from "./sessionService";
import { createEvent, getEvent } from "./eventService";

/** Drop undefined values so nothing undefined/null reaches Firestore. */
function compact<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== undefined && value !== null) out[key] = value;
  });
  return out as Partial<T>;
}

/** Fields of the training log that are owned by the performance draft. */
export function trainingLogBody(
  draft: PerformanceDraft,
  eventId: string | undefined,
  eventDay: number | undefined,
): Omit<TrainingLog, "timestamp" | "dateID"> {
  return compact({
    load: draft.load,
    muscular: draft.muscular,
    respiratory: draft.respiratory,
    rpe: draft.rpe,
    duration: draft.duration,
    trainingPerformed: draft.trainingPerformed,
    notes: draft.notes ?? "",
    tag: draft.tag ?? [],
    trainingTypeID: draft.trainingTypeID,
    pain: draft.pain,
    injured: draft.injured,
    performanceType: draft.performanceType,
    eventId,
    eventDay: eventId ? eventDay : undefined,
  }) as Omit<TrainingLog, "timestamp" | "dateID">;
}

/** Wire body of one result row (AddLogFlow.md §4.4 + PerformanceLog.md §1.2). */
export function resultBody(
  row: PerformanceResultDraft,
  trainingLogId: string,
  eventId: string | undefined,
  dateID: string,
  forUpdate: boolean,
): Omit<PerformanceLog, "timestamp" | "dateID"> {
  const body: Record<string, any> = {
    performanceTitle: "",
    performanceResult: "",
    performanceExpectation: row.performanceExpectation,
    notes: (row.notes ?? "").trim(),
    activity: {
      activityTypeID: row.activity.activityTypeID,
      activityName: row.activity.activityName,
      variables: row.activity.variables.map((v) =>
        compact({ name: v.name, measurement: v.measurement, unit: v.unit }),
      ),
      ...(row.activity.globalCategoryID && {
        globalCategoryID: row.activity.globalCategoryID,
      }),
    },
    performanceDate: dateID,
    linkedTrainingLogID: trainingLogId,
  };
  const tags = (row.tag ?? []).filter(Boolean);
  // On update always send the array so removed pills persist.
  if (tags.length > 0 || forUpdate) body.tag = tags;
  if (eventId) body.eventId = eventId;
  return body as Omit<PerformanceLog, "timestamp" | "dateID">;
}

/**
 * Composite write of a Performance (PerformanceLog.md §4). Not transactional;
 * callers may retry. Step order: (quick-create event) → training log →
 * results → niggle → day post links → session link. There is no `users/{uid}`
 * write.
 */
export async function addPerformance(
  userId: string,
  draft: PerformanceDraft,
  dateID: string,
): Promise<AddPerformanceResult> {
  if (!userId) throw new Error("User ID is required");
  if (!dateID) throw new Error("dateID is required");
  if (!draft.performanceType) throw new Error("performanceType is required");

  // 0. Quick-create event ("Create event" in the Link step) before the log.
  let eventId = draft.eventId;
  let eventDay = draft.eventDay;
  if (!eventId && draft.createEvent?.name.trim()) {
    eventId = await createEvent(userId, {
      name: draft.createEvent.name,
      type: draft.createEvent.type,
      date: draft.createEvent.date,
    });
    draft.eventId = eventId;
    eventDay = undefined;
  } else if (eventId && eventDay === undefined) {
    try {
      eventDay = eventDayIndex((await getEvent(userId, eventId)) ?? undefined, dateID);
    } catch (error) {
      console.error("Error resolving event day:", error);
    }
  }

  // 1. Training log — create, or update in place (upgrade / edit mode).
  let trainingLogId: string;
  const body = trainingLogBody(draft, eventId, eventDay);
  if (draft.existingTrainingLogId) {
    trainingLogId = draft.existingTrainingLogId;
    const ref = doc(db, "trainingLogs", userId, "logs", trainingLogId);
    const patch: Record<string, any> = { ...body };
    // Clear event fields that were removed in the editor.
    if (!eventId) patch.eventId = deleteField();
    if (!eventId || eventDay === undefined) patch.eventDay = deleteField();
    if (draft.trainingPerformed === undefined) {
      patch.trainingPerformed = deleteField();
    }
    await updateDoc(ref, patch);
  } else {
    trainingLogId = await addTrainingLog(userId, body, dateID);
  }

  // 2. Results.
  const resultIds: string[] = [];
  for (const row of draft.results) {
    if (row.id) {
      const patch: Record<string, any> = {
        ...resultBody(row, trainingLogId, eventId, dateID, true),
      };
      if (!eventId) patch.eventId = deleteField();
      await updateDoc(doc(db, "performanceLogs", userId, "logs", row.id), patch);
      resultIds.push(row.id);
    } else {
      const id = await addPerformanceLog(
        userId,
        resultBody(row, trainingLogId, eventId, dateID, false),
        dateID,
      );
      resultIds.push(id);
      row.id = id;
    }
  }

  // Rows removed in the editor.
  for (const removedId of draft.removedResultIds ?? []) {
    try {
      const existing = await getPerformanceLog(userId, removedId);
      await deletePerformanceLog(userId, removedId);
      await removeLogIDFromDayPost(
        userId,
        existing?.dateID ?? dateID,
        removedId,
        "performanceLogIDs",
      );
    } catch (error) {
      console.error("Error deleting removed result:", error);
    }
  }

  // 3. Optional niggle.
  let niggleLogId: string | undefined;
  if (draft.niggle) {
    niggleLogId = await addNiggleLog(
      userId,
      {
        pain: draft.niggle.pain,
        impact: draft.niggle.impact,
        notes: (draft.niggle.notes ?? "").trim(),
        linkedTrainingLogID: trainingLogId,
      },
      dateID,
    );
  }

  // 4. Day post links (training log skipped in upgrade/edit mode).
  if (!draft.existingTrainingLogId) {
    await addLogIDToDayPost(userId, dateID, trainingLogId, "trainingLogIDs");
  }
  for (const id of resultIds) {
    await addLogIDToDayPost(userId, dateID, id, "performanceLogIDs");
  }
  if (niggleLogId) {
    await addLogIDToDayPost(userId, dateID, niggleLogId, "niggleLogIDs");
  }

  // 5. Linked session.
  if (draft.linkedSessionID) {
    try {
      await sessionService.linkSessionToLog(
        userId,
        draft.linkedSessionID,
        trainingLogId,
        dateID,
      );
    } catch (error) {
      console.error("Error linking session to performance:", error);
    }
  }

  return { trainingLogId, resultIds, niggleLogId };
}

/**
 * Link (or unlink with `null`) a performance to an event — Events.md §3.4.
 * Updates the training log (`eventId`, `eventDay`, `performanceType`) and
 * every result with `linkedTrainingLogID == trainingLogId`. Only touches the
 * athlete's own logs.
 */
export async function linkPerformanceToEvent(
  uid: string,
  trainingLogId: string,
  eventId: string | null,
): Promise<void> {
  if (!uid || !trainingLogId) throw new Error("User ID and training log ID are required");
  const logRef = doc(db, "trainingLogs", uid, "logs", trainingLogId);

  const resultsSnap = await getDocs(
    query(
      collection(db, "performanceLogs", uid, "logs"),
      where("linkedTrainingLogID", "==", trainingLogId),
    ),
  );

  const batch = writeBatch(db);
  if (eventId) {
    const event = await getEvent(uid, eventId);
    if (!event) throw new Error("Event not found");
    const dateID = (await getTrainingLog(uid, trainingLogId))?.dateID;
    const day = dateID ? eventDayIndex(event, dateID) : undefined;
    batch.update(logRef, {
      eventId,
      eventDay: day !== undefined ? day : deleteField(),
      performanceType: event.type,
    });
    resultsSnap.docs.forEach((d) => batch.update(d.ref, { eventId }));
  } else {
    batch.update(logRef, { eventId: deleteField(), eventDay: deleteField() });
    resultsSnap.docs.forEach((d) => batch.update(d.ref, { eventId: deleteField() }));
  }
  await batch.commit();
}
